import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Award, Users, Calendar, BookOpen, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

/* ── Spring counter with premium easing ─────────────────── */
const AnimatedCounter = ({ end, duration = 3000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime;
    let raf;
    const animate = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      // Quartic easing out for a more premium finish
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * end));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [started, end, duration]);

  return (
    <span ref={ref} className="gold-shimmer font-serif" style={{ fontWeight: 700 }}>
      {count.toLocaleString('pt-BR')}{suffix}
    </span>
  );
};

/* ── Section ────────────────────────────────────────────── */
const ImpactCounters = () => {
  const { impactData, theme } = useLanguage();
  const isDark = theme === 'dark';
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const rawBgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  const bgY = useSpring(rawBgY, { stiffness: 60, damping: 22 });

  const icons = [Award, Users, Calendar, BookOpen];

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: isDark ? 'var(--bg-page)' : '#FAF7F2' }}
    >
      {/* Dynamic Background Glows */}
      <motion.div
        style={{
          y: bgY,
          position: 'absolute',
          inset: '-20%',
          background: isDark
            ? 'radial-gradient(circle at 50% 50%, rgba(184,134,11,0.08) 0%, transparent 60%)'
            : 'radial-gradient(circle at 50% 50%, rgba(184,134,11,0.05) 0%, transparent 60%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 border border-gold-subtle"
            style={{ background: isDark ? 'rgba(184, 134, 11, 0.1)' : 'rgba(184, 134, 11, 0.05)' }}
          >
            <Sparkles size={14} className="text-gold" />
            <span className="mono-text text-gold" style={{ fontSize: '0.72rem', letterSpacing: '0.12em' }}>
              RECONHECIMENTO & IMPACTO
            </span>
          </div>
          
          <h2 className="heading-hero mb-6">{impactData.title}</h2>
          <p className="body-large mx-auto" style={{ color: 'var(--text-secondary)', maxWidth: '640px' }}>
            {impactData.subtitle}
          </p>
        </motion.div>

        {/* Counters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {impactData.stats.map((stat, index) => {
            const IconComponent = icons[index % icons.length];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95, y: 40 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.15,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className="relative group h-full"
              >
                {/* Shine Animation Cover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-shine rounded-2xl pointer-events-none" />

                <div className="h-full p-8 rounded-2xl border transition-all duration-500 flex flex-col items-center text-center"
                  style={{ 
                    background: isDark ? 'rgba(30, 30, 30, 0.4)' : 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(12px)',
                    borderColor: 'rgba(184, 134, 11, 0.12)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(184, 134, 11, 0.4)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(184, 134, 11, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(184, 134, 11, 0.12)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <motion.div
                    className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center transition-all duration-500 group-hover:rotate-[360deg]"
                    style={{ 
                      background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(184, 134, 11, 0.2)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                    }}
                  >
                    <IconComponent size={30} style={{ color: 'var(--accent-gold-400)' }} />
                  </motion.div>

                  <div className="heading-hero mb-2" style={{ fontSize: 'clamp(2.25rem, 4vw, 3rem)' }}>
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} duration={3000} />
                  </div>

                  <p className="heading-3 mb-2" style={{ color: 'var(--text-primary)', fontSize: '1rem', letterSpacing: '0.02em' }}>
                    {stat.label}
                  </p>
                  <p className="body-small" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    {stat.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Elegant Bottom Line */}
        <motion.div
          className="mt-20 h-px w-full"
          initial={{ scaleX: 0, opacity: 0 }}
          whileInView={{ scaleX: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: 'circOut' }}
          style={{ 
            background: 'linear-gradient(90deg, transparent, rgba(184, 134, 11, 0.3), transparent)'
          }}
        />
      </div>
    </section>
  );
};

export default ImpactCounters;
